import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Calendar as CalendarIcon, MapPin, Users, ChevronLeft, ChevronRight, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const Calendar = () => {
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'meeting',
    start_date: '',
    end_date: '',
    attendees: '',
    location: ''
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await api.get('/calendar');
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to load events');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/calendar', formData);
      toast.success('Event created');
      setShowForm(false);
      setFormData({ title: '', description: '', event_type: 'meeting', start_date: '', end_date: '', attendees: '', location: '' });
      fetchEvents();
    } catch (error) {
      toast.error('Failed to create event');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await api.delete(`/calendar/${id}`);
      toast.success('Event deleted');
      setSelectedDate(null);
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const openFormForDate = (date) => {
    const dateStr = date.toISOString().slice(0, 16);
    setFormData({ ...formData, start_date: dateStr });
    setShowForm(true);
  };

  const getEventTypeColor = (type) => {
    const colors = {
      meeting: 'bg-blue-500',
      match: 'bg-green-500',
      scouting: 'bg-purple-500',
      signing: 'bg-white',
      other: 'bg-zinc-500'
    };
    return colors[type] || colors.other;
  };

  const getEventTypeBadge = (type) => {
    const colors = {
      meeting: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      match: 'bg-green-500/20 text-green-400 border-green-500/30',
      scouting: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      signing: 'bg-white/20 text-white border-white/30',
      other: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30'
    };
    return colors[type] || colors.other;
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    return { daysInMonth, startingDay };
  };

  const getEventsForDate = (date) => {
    return events.filter(event => {
      const eventDate = new Date(event.start_date);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const { daysInMonth, startingDay } = getDaysInMonth(currentMonth);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const isToday = (day) => {
    const today = new Date();
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return date.toDateString() === today.toDateString();
  };

  const renderCalendarDays = () => {
    const days = [];
    
    // Empty cells for days before the first of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-24 bg-black/20 border border-white/5"></div>);
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const dayEvents = getEventsForDate(date);
      const hasEvents = dayEvents.length > 0;
      
      days.push(
        <div
          key={day}
          onClick={() => setSelectedDate(date)}
          className={`h-24 p-1 border border-white/5 cursor-pointer transition-all hover:bg-white/5 ${
            isToday(day) ? 'bg-white/10 border-[#FFFFFF]/30' : 'bg-black/20'
          } ${selectedDate?.toDateString() === date.toDateString() ? 'ring-2 ring-[#FFFFFF]' : ''}`}
        >
          <div className={`text-sm font-bold mb-1 ${isToday(day) ? 'text-white' : 'text-white'}`}>
            {day}
          </div>
          <div className="space-y-0.5 overflow-hidden">
            {dayEvents.slice(0, 3).map((event, idx) => (
              <div
                key={idx}
                className={`text-[10px] truncate px-1 py-0.5 rounded ${getEventTypeColor(event.event_type)} text-black font-medium`}
              >
                {event.title}
              </div>
            ))}
            {dayEvents.length > 3 && (
              <div className="text-[10px] text-zinc-500">+{dayEvents.length - 3} more</div>
            )}
          </div>
        </div>
      );
    }
    
    return days;
  };

  const selectedDateEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  const upcomingEvents = useMemo(() => 
    events
      .filter(e => new Date(e.start_date) >= new Date())
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
      .slice(0, 5),
    [events]
  );

  return (
    <div className="animate-fade-in">
      <div className="relative bg-black/60 backdrop-blur-xl border-b border-white/5 overflow-hidden">
        <div className="hero-glow"></div>
        <div className="relative z-10 p-6 md:p-8 lg:p-12">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold uppercase tracking-tight leading-none text-white mb-2">Calendar</h1>
              <p className="text-lg text-zinc-300">{events.length} events scheduled</p>
            </div>
            <button
              onClick={() => setShowForm(true)}
              data-testid="add-event-btn"
              className="inline-flex items-center gap-2 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm px-6 py-3"
            >
              <Plus className="w-5 h-5" />
              Add Event
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 md:p-8 lg:p-12">
        {/* Event Form Modal */}
        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
            <div className="glass-card p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold uppercase text-white">New Event</h2>
                <button onClick={() => setShowForm(false)} className="text-zinc-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text"
                  required
                  placeholder="Event title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-3 text-white outline-none"
                />
                <textarea
                  rows="2"
                  placeholder="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-3 text-white outline-none"
                />
                <div className="grid grid-cols-2 gap-4">
                  <select
                    value={formData.event_type}
                    onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                    className="bg-zinc-900/50 border border-white/10 rounded-md px-4 py-3 text-white outline-none"
                  >
                    <option value="meeting">Meeting</option>
                    <option value="match">Match</option>
                    <option value="scouting">Scouting</option>
                    <option value="signing">Signing</option>
                    <option value="other">Other</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="bg-zinc-900/50 border border-white/10 rounded-md px-4 py-3 text-white outline-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">Start Date</label>
                    <input
                      type="datetime-local"
                      required
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      className="w-full bg-zinc-900/50 border border-white/10 rounded-md px-4 py-3 text-white outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">End Date</label>
                    <input
                      type="datetime-local"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="w-full bg-zinc-900/50 border border-white/10 rounded-md px-4 py-3 text-white outline-none"
                    />
                  </div>
                </div>
                <input
                  type="text"
                  placeholder="Attendees (comma separated)"
                  value={formData.attendees}
                  onChange={(e) => setFormData({ ...formData, attendees: e.target.value })}
                  className="w-full bg-zinc-900/50 border border-white/10 rounded-md px-4 py-3 text-white outline-none"
                />
                <div className="flex gap-3">
                  <button type="submit" className="flex-1 bg-white text-black hover:bg-zinc-200 font-bold uppercase px-6 py-3 rounded-sm">
                    Save Event
                  </button>
                  <button type="button" onClick={() => setShowForm(false)} className="bg-white/10 text-white hover:bg-white/20 px-6 py-3 rounded-sm">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar Grid */}
          <div className="lg:col-span-2">
            <div className="glass-card p-4">
              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-4">
                <button onClick={prevMonth} className="p-2 hover:bg-white/10 rounded transition-colors">
                  <ChevronLeft className="w-5 h-5 text-white" />
                </button>
                <h2 className="text-xl font-bold text-white">
                  {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </h2>
                <button onClick={nextMonth} className="p-2 hover:bg-white/10 rounded transition-colors">
                  <ChevronRight className="w-5 h-5 text-white" />
                </button>
              </div>
              
              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-px mb-1">
                {dayNames.map(day => (
                  <div key={day} className="text-center text-xs font-bold text-zinc-500 uppercase py-2">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar Days */}
              <div className="grid grid-cols-7 gap-px">
                {renderCalendarDays()}
              </div>
            </div>
          </div>

          {/* Selected Date Events */}
          <div>
            <div className="glass-card p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">
                  {selectedDate ? selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }) : 'Select a date'}
                </h3>
                {selectedDate && (
                  <button
                    onClick={() => openFormForDate(selectedDate)}
                    className="text-white hover:text-[#E5E5E5] text-sm font-bold"
                  >
                    + Add
                  </button>
                )}
              </div>
              
              {selectedDate ? (
                selectedDateEvents.length > 0 ? (
                  <div className="space-y-3">
                    {selectedDateEvents.map(event => (
                      <div key={event.id} className="p-3 bg-white/5 rounded-lg border border-white/5">
                        <div className="flex items-start justify-between mb-2">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${getEventTypeBadge(event.event_type)}`}>
                            {event.event_type}
                          </span>
                          <button
                            onClick={() => handleDelete(event.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <h4 className="font-bold text-white mb-1">{event.title}</h4>
                        {event.description && (
                          <p className="text-xs text-zinc-400 mb-2">{event.description}</p>
                        )}
                        <div className="space-y-1 text-xs text-zinc-500">
                          <div className="flex items-center gap-1">
                            <CalendarIcon className="w-3 h-3" />
                            {new Date(event.start_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                          {event.location && (
                            <div className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {event.location}
                            </div>
                          )}
                          {event.attendees && (
                            <div className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {event.attendees}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-zinc-500">
                    <CalendarIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No events on this day</p>
                    <button
                      onClick={() => openFormForDate(selectedDate)}
                      className="mt-2 text-white hover:text-[#E5E5E5] text-sm font-bold"
                    >
                      + Add Event
                    </button>
                  </div>
                )
              ) : (
                <div className="text-center py-8 text-zinc-500">
                  <CalendarIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Click on a date to view events</p>
                </div>
              )}
            </div>

            {/* Upcoming Events */}
            <div className="glass-card p-4 mt-6">
              <h3 className="text-lg font-bold text-white mb-4">Upcoming Events</h3>
              {upcomingEvents.length > 0 ? (
                <div className="space-y-2">
                  {upcomingEvents
                    .map(event => (
                      <div key={event.id} className="flex items-center gap-3 p-2 bg-white/5 rounded">
                        <div className={`w-2 h-2 rounded-full ${getEventTypeColor(event.event_type)}`}></div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate">{event.title}</p>
                          <p className="text-xs text-zinc-500">
                            {new Date(event.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </p>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-zinc-500">No upcoming events</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calendar;
